<script setup lang="ts">

import type { OnboardingStep } from '@/types/onboarding'
import BaseButton from '@/components/base/BaseButton.vue'
import { twMerge } from 'tailwind-merge'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
	steps: OnboardingStep[]
	currentStep: OnboardingStep
	isLoading?: boolean
}>()

defineEmits<{
	(e: 'setStep', index: number): void
	(e: 'goNext'): void
}>()

const { bottomNumber } = useFullScreenDimensions()
const videoError = ref(false)
const videoReady = ref(false)

const nextStep = computed(() => {
  const currentIndex = props.currentStep.index
  return props.steps.find(step => step.index === currentIndex + 1)
})

watch(() => props.currentStep, () => {
  videoError.value = false
  videoReady.value = false
})

const handleVideoError = () => {
  videoError.value = true
}

const handleVideoCanPlay = () => {
  videoReady.value = true
}
</script>

<template>
  <div
    class="flex-1 flex flex-col items-center justify-between h-full relative w-full overflow-hidden"
  >
    <transition
      name="fade"
      mode="out-in"
    >
      <div
        :key="currentStep.index"
        class="absolute left-0 right-0 top-0 h-[75%] w-full"
      >
        <!-- Always load the image as base layer -->
        <img
          class="absolute left-0 right-0 top-0 h-full object-cover w-full"
          :src="currentStep.image"
          :alt="currentStep.title"
        >
        <!-- Video overlay when available - starts loading immediately -->
        <transition name="fade">
          <video
            v-if="currentStep.video && !videoError"
            class="absolute left-0 right-0 top-0 h-full object-cover w-full"
            :src="currentStep.video"
            autoplay
            loop
            muted
            playsinline
            preload="metadata"
            @error="handleVideoError"
            @loadeddata="handleVideoCanPlay"
          />
        </transition>
      </div>
    </transition>

    <!-- Preload next step's video and image in background -->
    <template v-if="nextStep">
      <img
        v-if="nextStep.image"
        class="absolute opacity-0 pointer-events-none w-0 h-0"
        :src="nextStep.image"
        :alt="`Preload ${nextStep.title}`"
      >
      <video
        v-if="nextStep.video"
        class="absolute opacity-0 pointer-events-none w-0 h-0"
        :src="nextStep.video"
        muted
        playsinline
        preload="auto"
      />
    </template>

    <div
      class="absolute left-0 right-0 bottom-0 h-[60%] w-full"
      style="background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 11.49%, #000 47.55%)"
    />
    <div
      class="px-6 mt-auto flex w-full flex-col gap-y-6 relative"
      :style="{paddingBottom: `${bottomNumber}px`}"
    >
      <transition-group
        name="slider"
      >
        <div
          :key="`title-${currentStep.index}`"
          :class="twMerge('font-pilat font-bold text-shadow-2', currentStep.index === 0 ? 'text-[66px] leading-[48px] text-center pb-8' : 'text-[36px] leading-9')"
          v-html="currentStep.title"
        />
        <div
          v-if="currentStep.description.length > 0"
          :key="`description-${currentStep.index}`"
          class="text-base font-semibold text-white/[.60]"
          v-html="currentStep.description"
        />
      </transition-group>
      <div class="flex gap-x-3 justify-center">
        <div
          v-for="step in steps"
          :key="step.index"
          class="h-2 w-2 min-w-[8px] cursor-pointer rounded-full"
          :class="step.index <= currentStep.index ? 'bg-teal-primary' : 'bg-light-white'"
          @click="$emit('setStep', step.index)"
        />
      </div>
      <BaseButton
        style-type="primary-pink"
        class="w-full"
        :loading="isLoading"
        @click="$emit('goNext')"
      >
        {{ currentStep.buttonLabel ?? $t('onboarding.next', 'Next') }}
      </BaseButton>
    </div>
  </div>
</template>

<style scoped>
.bottom-shade {
	background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 11.49%, #000 47.55%);
}

.background-green-linear {
	background: linear-gradient(180deg, #E2FFA5 0%, #C6F36B 98%);
}

/* Slider transition classes */
.slider-enter-active,
.slider-leave-active {
	transition: transform 0.35s ease;
}

.slider-enter-from {
	transform: translateX(100%);
}

.slider-leave-to {
	transform: translateX(-100%);
}

/* To ensure the leaving image stays in position while sliding */
.slider-leave-active {
	position: absolute;
}

/* Custom transition for the duck image on step 0 */
.slider-leave-active .background-green-linear {
	transition: transform 0.35s ease;
	transform: translateX(-100%);
}

.fade-enter-active, .fade-leave-active {
	transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
	opacity: 0;
}

.fade-delayed-enter-active,
.fade-delayed-leave-active {
	transition: opacity 0.35s ease;
	transition-delay: 0.15s; /* Add delay before the transition starts */
}

.fade-delayed-enter-from,
.fade-delayed-leave-to /* .fade-leave-active in <2.1.8 */
{
	opacity: 0;
}
</style>

